import { PlayerIdentity, PlayerRatingScope } from 'components/ReviewAudit/PublicRatings'
import EditorActions from 'components/Common/EditorActions'
import modalSelectMenuProps from "components/Common/modalSelectMenuProps"
import React, { useState, useContext, useRef, useEffect, useMemo } from "react"
import { store as socketStore } from "components/Common/Socket/store"
import {
  Card,
  CardBody,
  CardText,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  ModalFooter,
  Label,
  FormGroup,
} from "reactstrap"

import { Formik } from "formik"
import { Link } from "react-router-dom"
import { Badge } from "reactstrap"
import Select from "react-select"
import { connect } from "react-redux"
import { matchSendFailure, replaceMatchEntries, swapMatchEntries } from "../Tabs/Matches/api"
import { isNumber } from "lodash"
import { getParticipantGameLevel } from "./participantLevel"
import { renderReplacementOption, replacementOption, replacementPeople } from "./replacementOption"
import { participantAccountPath } from "./participantDisplay"
import { completesGroupBye } from "./groupByeReplacement"
import { accountNumber, entryKeys, entrySlot, isPlaced, participantKey, placedKeys } from "./participantIdentity"
import { swapScope } from "./swapScope"
import { seedless } from "./seeds"

// P54: a withdrawn entry stays on the Main List as a soft delete — never a Replace or Swap candidate (the api's `confirmedEntries`).
const activeEntries = list => (list || []).filter(entry => !["WITHDRAWN", "DELETED"].includes(entry?.status))

const actionTypes = [
  { label: "Replace", value: "replace" },
  { label: "Swap", value: "swap" },
]


const Slot = ({
  Actions,
  renderCard,
  className,
  position,
  status,
  user = {},
  partner,
  isWildCard,
  bye,
  positionClick,
  slotType,
  gameType,
  draw,
  winner,
  createdBy,
  createdById,
  key,
  seeded,
  paid,
  competitionPosition,
  mainList,
  replacePlayer,
  displayTeamAvg,
  bottomSpace,
  playerEntries,
  partnerEntries,
  partnerId,
  matchId,
  fetchMatches,
  role,
  expected,
  competitionId,
  emptyWC,
  matches = [],
  competitionDetails,
  qualPlayerNumber,
  currency,
}) => {
  const [modal, setModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  const toggle = () => {
    if (mounted.current) {
      setModal(value => !value)
      setSaveError(null)
    }
  }
  const globalState = useContext(socketStore)
  const { state: { socket: { socketClientId } = {} } = {} } = globalState

  const isTypeSolo = gameType === "doubles" ? "D" : "S"
  const isPhaseMatch = draw === "eliminatory" && competitionDetails?.drawModel === "groups";

  const currentMatch = matches?.flat(Infinity)?.find(m => m._id === matchId);

  const formatPayloadForUpdate = data => {
    const roleData = data?.player ? data?.player : data?.opponent
    const roleName = data?.player ? "player" : "opponent"

    let payload = {
      // a Tenis10 child goes as the child, on its parent's account (participantIdentity.js, PRD 16 LR11c)
      [roleName]: entrySlot(roleData),
      ...(gameType === "doubles" && {
        [`${roleName}Partner`]: data[roleName]?.partner,
      }),
    }

    if (roleData?.type === "BYE") {
      payload = {
        [roleName]: {
          type: "BYE",
        },
        ...(gameType === "doubles" && {
          [`${roleName}Partner`]: {
            type: "BYE",
          },
        }),
      }
    }

    // Replace and Swap name the player or team the dialog shows as current; Accounts
    // refuses the change when this side holds someone else by then.
    return expected ? { ...payload, expected } : payload
  }

  const returnUserName = user => {
    const name = []
    const { firstName, lastName, type } = user

    if (type === "BYE" && !firstName && !lastName) {
      return "BYE"
    } else {
      firstName ? name?.push(firstName) : name?.push("")
      lastName ? name?.push(lastName) : name?.push("")
    }

    return name?.join(" ")
  }

  let allCompetitionEntries =
    (competitionDetails?.mainList || []).concat(
      competitionDetails?.waitingList
    ) || []

  // W rides on the entry of the slot's own participant: a Tenis10 child's by its profileId (PRD 16 LR11c).
  const handleWIndicator = (person, role) => {
    const key = participantKey(person)
    const currentEntry = key && allCompetitionEntries?.find(entry => entryKeys(entry).includes(key))
    if (currentEntry?.[role]?.w === true) {
      return (
        <Badge className="mr-1" color="success">
          <p className="text-dark mb-0">W </p>
        </Badge>
      )
    }
  }

  if (competitionPosition) {
    user.competitionRank = competitionPosition
  }

  const userGameLevel = getParticipantGameLevel(user, gameType)
  const userOldPoints = isNumber(user?.points)
    ? user?.[gameType === "doubles" ? "pointsDouble" : "points"]
    : null
  const userOldRank = isNumber(user?.rank)
    ? user?.[gameType === "doubles" ? "rankDouble" : "rank"]
    : null

  const partnerGameLevel = getParticipantGameLevel(partner, gameType)
  const partnerOldPoints = isNumber(partner?.points)
    ? partner?.[gameType === "doubles" ? "pointsDouble" : "points"]
    : null
  const partnerOldRank = isNumber(partner?.rank)
    ? partner?.[gameType === "doubles" ? "rankDouble" : "rank"]
    : null

  // Who the draw holds, by participant key (participantIdentity.js): a Tenis10 child, never its sibling
  // under the same parent account (PRD 16 LR11c).
  const placed = placedKeys(matches)

  // Keys of the current slot's player (and partner) — exclude from select list
  const currentSlotKeys = [participantKey(user), participantKey(partner)].filter(Boolean);

  // The exact team occupying this slot — only that entry is excluded from the
  // replace selector. An entry that merely shares one player with the slot
  // (e.g. same player with a new partner) is a legitimate replacement.
  const isCurrentSlotTeam = entry => isPlaced(entry, currentSlotKeys)

  // Participants already allocated in phase matches (eliminatory) outside this slot — a player
  // sitting in the very slot being replaced must not block their own team's replacement.
  const phaseMatches = matches?.filter(({ competition }) => competition?.drawType === "eliminatory") || [];
  const allocatedElsewherePhaseKeys = placedKeys(phaseMatches).filter(
    key => !currentSlotKeys.includes(key)
  );

  // BYE only takes the place of a player or team that faces another player or team. Accounts
  // refuses it on an open, pending or BYE place and opposite one (BYE_ON_OPEN_SLOT_NOT_PERMITTED,
  // BYE_AGAINST_OPEN_SLOT_NOT_PERMITTED, BYE_ON_BOTH_MATCH_SIDES_NOT_PERMITTED).
  const opposite = currentMatch?.[role === "player" ? "opponent" : "player"]
  const byeChoice = user?.userId && opposite?.userId ? [{ type: "BYE" }] : []

  const candidates = useMemo(() => activeEntries(mainList), [mainList])
  const swap = useMemo(() => swapScope({ matches, matchId, role, mainList: candidates }), [matches, matchId, role, candidates])

  const filteredPlayers = (actionType) => {
    if (actionType === "swap") return swap.candidates

    if (isPhaseMatch) return [...byeChoice, ...candidates.filter(
      entry => {
        // Exclude the slot's own team and participants already allocated in other phase matches
        if (isCurrentSlotTeam(entry)) return false;
        if (allocatedElsewherePhaseKeys.includes(entryKeys(entry)[0])) return false;
        return isPlaced(entry, placed)
      }
    )]

    return [...byeChoice, ...candidates.filter(
      entry => {
        // Exclude only the slot's own team — partial overlaps are valid replacements
        if (isCurrentSlotTeam(entry)) return false;
        if (completesGroupBye(entry, { matches, match: currentMatch, role })) return true;
        return !isPlaced(entry, placed)
      }
    )];
  }

  return (
    <div className="flex-grow-1">
      {renderCard ? renderCard({ openReplacement: toggle }) : <Card
        className={`slot ${bottomSpace && `pb-4`} ${
          emptyWC && `emptySlotHeight`
        } ${className} ${winner && "winner"} ${bye && "bye"}`}
        key={key}
      >
        <CardBody className="d-flex align-items-center">
          {position && (
            <div className="position" onClick={positionClick}>
              {position}
            </div>
          )}
          <div className="d-flex flex-column justify-content-center flex-grow-1">
            <div className="d-flex justify-content-between">
              {!emptyWC &&
                (user?.userId || user?.type === "BYE" ? (
                  <div>
                    <p className="mb-0">
                      {/* A Tenis10 child's slot has no account of its own, and its userId is its parent's (PRD 16 LR11b, register E39 (1)). */}
                      {user.userId && !user.profileId && (
                        <>
                          {participantAccountPath(user) ? <Link
                            target="_blank"
                            to={participantAccountPath(user)}
                          >
                            <span className="color-purple mr-1">
                              #{user.userId}
                            </span>
                          </Link> : <span className="color-purple mr-1">#{user.userId}</span>}
                          {" — "}
                        </>
                      )}
                      {/* RELEASE-TENIS10 § 11 P78: the child's own number there instead (no account to link to) */}
                      {user.profileId && accountNumber(user) != null && <><span className="color-purple mr-1">#{accountNumber(user)}</span>{" — "}</>}
                      {user.userId && playerEntries > 1 && (
                        <Badge
                          color="primary"
                          className="mr-1"
                        >{`${playerEntries} `}</Badge>
                      )}
                      {handleWIndicator(user, "user")}
                      <strong
                        onClick={() => replacePlayer && toggle()}
                        className="pointer"
                      >
                        {returnUserName(user)}
                      </strong>{" "}
                      {/* P54: a child (Tenis10) never shows a seed */}
                      {seeded &&
                        !seedless(user, competitionDetails) &&
                        user?.competitionRank &&
                        `(${user?.competitionRank}) `}{" "}
                      {!user?.profileId && user?.location?.name && <>from {user.location.name}</>}
                    </p>
                  </div>
                ) : (
                  qualPlayerNumber && (
                    <p className="mb-0">Q {qualPlayerNumber}</p>
                  )
                ))}

              {Actions && <Actions />}
            </div>
            {/* a Tenis10 child's slot shows no contact: the parent's are on its Entries row (register E39 (1)) */}
            {!user?.profileId && <CardText className="fs-10 text-muted mb-1 d-flex">
              <p className="mb-0">{user && user.email}</p>
              <p className="mb-0  ml-2">{user && user.phone}</p>
            </CardText>}
            {/* A Tenis10 child's slot carries its parent's rank, points and level, which are not the child's (PRD 16 LR11b, register
                E39 (1)); nor the competition's category, which every child of it has (RELEASE-TENIS10 § 11 P90). */}
            {!emptyWC && !user?.profileId && (
              <CardText className="mb-0">
                <div className="flex justify-content-between">
                  <div className="">
                    Rank-{isTypeSolo}
                    <p className="font-weight-bold mb-0">
                      {userOldRank ||
                        user?.rank?.[gameType] ||
                        Number(user?.rank) ||
                        "N/A"}
                    </p>
                  </div>
                  <div className="mr-2">
                    Points-{isTypeSolo}
                    <p className="font-weight-bold mb-0">
                      {userOldPoints ||
                        user?.points?.[gameType] ||
                        user?.userPoints ||
                        "N/A"}
                    </p>
                  </div>
                  <div className="">
                    Level-{isTypeSolo}
                    <p className="font-weight-bold mb-0">
                      {userGameLevel ?? "N/A"}
                    </p>
                  </div>
                  <div className="">
                    Plan
                    <p className="font-weight-bold text-capitalize mb-0">
                      {user?.membership?.plan || "N/A"}
                    </p>
                  </div>

                  {user?.registrationFee >= 0 && (
                    <div className="">
                      SSF
                      <p className="font-weight-bold mb-0">
                        {user?.registrationFee} {currency && currency}
                        {user.registrationFee > 0 && (
                          <span
                            className={`ml-1 badge badge-${
                              [undefined, true].includes(paid)
                                ? "success"
                                : "danger"
                            }`}
                          >
                            {[undefined, true].includes(paid)
                              ? "PAID"
                              : "UNPAID"}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  {/* {seeded && (
                  <div className="mr-2">
                    <i class="bx bxs-badge-check mr-1 color-gold"></i>
                    Favorite
                  </div>
                )} */}
                </div>
              </CardText>
            )}
            {partner && (
              <div className="partner">
                <div className="d-flex justify-content-between">
                  {partner?.userId || partner?.type === "BYE" ? (
                    <div>
                      <Link
                        target="_blank"
                        to={`/accounts/players/${partner?.id}`}
                      >
                        <span className="color-purple">
                          #{partnerId && partnerId}
                        </span>
                      </Link>
                      {" — "}
                      {partnerId && partnerEntries > 1 && (
                        <Badge color="primary">{partnerEntries}</Badge>
                      )}
                      {handleWIndicator(partner, "partner")}
                      <strong
                        onClick={() => replacePlayer && toggle()}
                        className="pointer"
                      >
                        {returnUserName(partner)}
                      </strong>{" "}
                      {seeded &&
                        user?.competitionRank &&
                        `(${user?.competitionRank}) `}{" "}
                      from {partner && partner?.location?.name}
                    </div>
                  ) : (
                    qualPlayerNumber && (
                      <p className="mb-0">Q {qualPlayerNumber}</p>
                    )
                  )}
                </div>
                <CardText className="fs-10 text-muted mb-1 d-flex">
                  <p className="mb-0">{partner && partner.email}</p>
                  <p className="mb-0  ml-2">{partner && partner.phone}</p>
                </CardText>
                <CardText className="mb-0">
                  <div className="flex justify-content-between">
                    <div className="">
                      Rank-{isTypeSolo}
                      <p>
                        <strong>
                          {partnerOldRank ||
                            partner.rank?.[gameType] ||
                            partner?.rank}
                        </strong>
                      </p>
                    </div>
                    <div className="">
                      Points-{isTypeSolo}
                      <p>
                        <strong>
                          {partnerOldPoints ||
                            partner.points?.[gameType] ||
                            partner?.userPoints}
                        </strong>
                      </p>
                    </div>
                    <div>
                      Level-{isTypeSolo}
                      <p>
                        <strong>
                          {partnerGameLevel}
                        </strong>
                      </p>
                    </div>
                    <div className="text-capitalize">
                      Plan
                      <p>
                        <strong>{partner && partner?.membership?.plan}</strong>
                      </p>
                    </div>
                    {partner?.registrationFee >= 0 && (
                      <div className="">
                        SSF
                        <p>
                          <strong>
                            {partner.registrationFee} {currency && currency}
                          </strong>
                          {partner.registrationFee > 0 && (
                            <span
                              className={`ml-1 badge badge-${
                                [undefined, true].includes(paid)
                                  ? "success"
                                  : "danger"
                              }`}
                            >
                              {[undefined, true].includes(paid)
                                ? "PAID"
                                : "UNPAID"}
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </CardText>
              </div>
            )}
            {(user?.groupName || competitionPosition) && (
              <div>
                <hr className="mt-0 mb-2" />
                <div class="flex justify-content-between">
                  {competitionPosition && (
                    <div className="text-right">
                      Competition Position:{" "}
                      <strong>#{competitionPosition}</strong>
                    </div>
                  )}
                  {user?.groupName && user?.groupPosition && (
                    <div className="">
                      Group Position:{" "}
                      <span className="font-weight-bold mb-0">
                        {user.groupName}
                        {user.groupPosition}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {slotType === "STANDARD" && createdBy && (
              <CardText className="addedBy">
                <span className="createdByTxt">Created by: </span> {createdBy}
                {createdById ? ` #${createdById}` : ""}
              </CardText>
            )}
            {status === "PENDING" && (
              <CardText className="pendingStatus">
                <p>{status}</p>
              </CardText>
            )}
            <CardText>
              <span className="wildCard">
                {isWildCard && (
                  <span>{slotType === "WILD_CARD" && "Wild card"}</span>
                )}
              </span>
            </CardText>

            {emptyWC && (
              <CardText>
                <span className="wildCard">
                  <span>Wild card</span>
                </span>
              </CardText>
            )}
          </div>
          {partner && displayTeamAvg && (
            <div>
              <p className="mb-0">
                Avg. Level:{" "}
                {(getParticipantGameLevel(user, "doubles") +
                  getParticipantGameLevel(partner, "doubles")) /
                  2}
              </p>
              <p className="mb-0">
                Avg. Points:{" "}
                {((user?.points?.doubles || userOldPoints) +
                  (partner?.points?.doubles || partnerOldPoints)) /
                  2}
              </p>
              <p className="mb-0">
                Avg. Rank:{" "}
                {((user?.rank?.doubles || userOldRank) +
                  (partner?.rank?.doubles || partnerOldRank)) /
                  2}
              </p>
            </div>
          )}
        </CardBody>
      </Card>}

      <Modal scrollable isOpen={modal} toggle={toggle} size="lg" className="main-draw-replace-dialog">
        <Formik
          initialValues={{
            player: "",
            opponent: "",
            actionType: "replace",
          }}
        >
          {({ values, setFieldValue, dirty }) => {
            return (
              <PlayerRatingScope players={modal ? [user, partner, ...filteredPlayers(values.actionType).flatMap(replacementPeople)] : []}>
                <ModalHeader toggle={toggle} className="text-capitalize">
                  {values?.actionType} {partner?.userId ? "team" : "player"}
                </ModalHeader>

                <ModalBody>
                  {saveError && <div role="alert" className="alert alert-danger">{saveError}</div>}
                  <div className="p-3 mb-3 bg-light rounded">
                    <small className="text-muted">Current {partner?.userId ? "team" : "player"}</small>
                    {[user, partner].filter(person => person?.userId || person?.type).map((person, index) => (
                      <div key={index}><PlayerIdentity player={person} to={null} ><strong>{returnUserName(person) || person.type}</strong></PlayerIdentity>{accountNumber(person) != null ? ` #${accountNumber(person)}` : ""}</div>
                    ))}
                  </div>
                  {isPhaseMatch && (
                    <FormGroup>
                      <Label htmlFor={`replacement-action-${matchId}-${role}`} className="text-capitalize">Action type</Label>
                      <Select
                        {...modalSelectMenuProps}
                        menuPortalTarget={document.body}
                        inputId={`replacement-action-${matchId}-${role}`}
                        name="actionType"
                        options={actionTypes.filter(({ value }) => value !== "swap" || swap.offered)}
                        onChange={({ value }) => {
                          setFieldValue("actionType", value)
                          setFieldValue("player", "")
                          setFieldValue("opponent", "")
                        }}
                        defaultValue={actionTypes.find(
                          ({ value }) => value === values?.actionType
                        )}
                      />
                    </FormGroup>
                  )}

                  <FormGroup>
                    <Label htmlFor={`replacement-target-${matchId}-${role}`} className="text-capitalize">
                      {values?.actionType === "swap" ? "Swap" : "Replace"} with
                    </Label>
                    <Select
                      {...modalSelectMenuProps}
                      menuPortalTarget={document.body}
                      inputId={`replacement-target-${matchId}-${role}`}
                      placeholder="Search by name or player ID"
                      options={filteredPlayers(values?.actionType)?.map(replacementOption)}
                      formatOptionLabel={renderReplacementOption}
                      value={values[role] ? replacementOption(values[role]) : null}
                      onChange={({ value }) => {
                        setFieldValue(
                          role === "player" ? "player" : "opponent",
                          value
                        )
                      }}
                    />
                  </FormGroup>
                </ModalBody>
                <ModalFooter><EditorActions dirty={dirty} pending={saving} saveLabel="Save">
                  <Button color="secondary" onClick={toggle}>
                    Cancel
                  </Button>{" "}
                  <Button
                    disabled={saving || !(values?.player || values?.opponent)}
                    color="primary"
                    onClick={async () => {
                      setSaving(true)
                      setSaveError(null)
                      try {
                      if (values.actionType === "swap") {
                        await swapMatchEntries(
                          matchId,
                          competitionDetails,
                          formatPayloadForUpdate(values),
                          socketClientId
                        )
                      }

                      if (values.actionType === "replace") {
                        await replaceMatchEntries(
                          matchId,
                          competitionDetails,
                          formatPayloadForUpdate(values),
                          socketClientId
                        )
                      }

                      if (!mounted.current) return
                      await fetchMatches({
                        competitionId: competitionId,
                      })
                      toggle()
                      } catch (error) {
                        if (mounted.current) setSaveError(matchSendFailure(error, "selection is"))
                      } finally {
                        if (mounted.current) setSaving(false)
                      }
                    }}
                  >
                    Save
                  </Button>
                </EditorActions></ModalFooter>
              </PlayerRatingScope>
            )
          }}
        </Formik>
      </Modal>
    </div>
  )
}

const mapStateToProps = ({ matches: { matches = [] } = {} }) => ({
  matches,
})

export default connect(mapStateToProps, null)(Slot)
